import { tokenStore } from "./tokenStore";
import { API_URL } from "./constants";

let userRefreshPromise: Promise<boolean> | null = null;
let adminRefreshPromise: Promise<boolean> | null = null;

export async function refreshUserToken(): Promise<boolean> {
  if (userRefreshPromise) {
    return userRefreshPromise;
  }

  userRefreshPromise = (async () => {
    try {
      const response = await fetch(`${API_URL}/auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      });

      if (!response.ok) {
        tokenStore.setUserToken(null);
        return false;
      }

      const data = await response.json();
      if (data.access_token) {
        tokenStore.setUserToken(data.access_token);
        return true;
      }

      tokenStore.setUserToken(null);
      return false;
    } catch {
      tokenStore.setUserToken(null);
      return false;
    } finally {
      userRefreshPromise = null;
    }
  })();

  return userRefreshPromise;
}

export async function refreshAdminToken(): Promise<boolean> {
  if (adminRefreshPromise) {
    return adminRefreshPromise;
  }

  adminRefreshPromise = (async () => {
    try {
      const response = await fetch(`${API_URL}/admin/auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      });

      if (!response.ok) {
        tokenStore.setAdminToken(null);
        return false;
      }

      const data = await response.json();
      if (data.access_token) {
        tokenStore.setAdminToken(data.access_token);
        return true;
      }

      tokenStore.setAdminToken(null);
      return false;
    } catch {
      tokenStore.setAdminToken(null);
      return false;
    } finally {
      adminRefreshPromise = null;
    }
  })();

  return adminRefreshPromise;
}

export async function apiFetch(
  url: string,
  options: RequestInit = {},
  _isRetry = false,
): Promise<Response> {
  const isAbsolute = url.startsWith("http://") || url.startsWith("https://");
  const targetUrl = isAbsolute
    ? url
    : `${API_URL}${url.startsWith("/") ? "" : "/"}${url}`;

  const headers = new Headers(options.headers || {});
  const isBrowser = typeof window !== "undefined";
  const isAdminCall = isBrowser
    ? window.location.pathname.startsWith("/admin")
    : /\/admin(\/|$|\?)/.test(targetUrl);

  const token = isAdminCall
    ? tokenStore.getAdminToken()
    : tokenStore.getUserToken();
  if (token) {
    headers.set("Authorization", `Bearer ${token}`);
  }

  // Ensure default JSON content type if body is present and not FormData
  if (
    options.body &&
    !(options.body instanceof FormData) &&
    !headers.has("Content-Type")
  ) {
    headers.set("Content-Type", "application/json");
  }

  // Set up AbortController for a 15-second request timeout
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 15000);

  try {
    const response = await fetch(targetUrl, {
      ...options,
      headers,
      credentials: "include",
      signal: controller.signal,
    });

    // Handle 401 Unauthorized errors with silent token refresh, excluding auth actions themselves
    if (response.status === 401 && !_isRetry) {
      const isAuthRoute =
        targetUrl.includes("/auth/login") ||
        targetUrl.includes("/auth/refresh") ||
        targetUrl.includes("/auth/logout") ||
        targetUrl.includes("/admin/auth/login") ||
        targetUrl.includes("/admin/auth/refresh") ||
        targetUrl.includes("/admin/auth/logout");

      if (!isAuthRoute) {
        const refreshed = isAdminCall
          ? await refreshAdminToken()
          : await refreshUserToken();
        if (refreshed) {
          return apiFetch(url, options, true);
        }
      }
    }

    return response;
  } finally {
    clearTimeout(timeoutId);
  }
}
